import React, { useEffect, useState } from "react";
import { Box, IconButton, Typography, Button, TextField, MenuItem, Select, Checkbox, FormControl } from "@mui/material";
import { State } from '../../state/reducers';
import { useSelector } from "react-redux";
import { System } from '../../state/types';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

const TextFieldComponent = ({
  label,
  value,
  type,
  onChange
}: {
  label: string;
  value: string | number | undefined | true | any;
  type: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <Box sx={{ mb: 0.3, width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <TextField
        value={value}
        type={type}
        onChange={onChange}
        fullWidth
        sx={{
          fontSize: '0.875rem',
          backgroundColor: 'white',
          borderRadius: '5px',
          color: 'black',
          width: '90%'
        }}
        inputProps={{
          style: {
            padding: '5px 10px',
            color: 'black',
            width: '100%'
          },
        }}
      />
    </Box>
  );
};

const ImageFilterRow = ({
  label,
  filter,
  onChange,
  comparisonOperators
}: {
  label: string;
  filter: ImageFilter;
  onChange: (newValue: ImageFilter) => void;
  comparisonOperators: string[];
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: '95%' }}>
    <Checkbox
      checked={filter?.checked || false}
      onChange={(e) => onChange({ ...filter, checked: e.target.checked })}
      size="small"
      sx={{
        mt: 2,
        '&.Mui-checked': { color: "darkred" },
      }}
    />
    <Select
      value={filter?.operator || "="}
      onChange={(e) => onChange({ ...filter, operator: e.target.value })}
      sx={{
        fontSize: '0.875rem',
        width: 'auto',
        maxWidth: '60px',
        padding: 0,
        mt: 2,
        backgroundColor: 'white',
        borderRadius: '5px',
        color: 'black',
        '& .MuiSelect-select': { padding: '5px 10px' },
        '& .MuiSvgIcon-root': { color: 'black' },
      }}
    >
      {comparisonOperators.map((op) => (
        <MenuItem key={op} value={op}>{op}</MenuItem>
      ))}
    </Select>
    <TextFieldComponent
      label={label}
      type="integer"
      value={filter?.imageCount ?? 0}
      onChange={(e) => onChange({ ...filter, imageCount: Number(e.target.value) })}
    />
  </Box>
);

const DropdownField = ({
  label,
  value,
  onChange,
  options,
  renderValueMap
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  renderValueMap?: Record<string, string>;
}) => (
  <FormControl fullWidth sx={{ mb: 0.3 }}>
    <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      renderValue={(selected) =>
        renderValueMap ? (renderValueMap[selected] || "Any") : (selected || "All")
      }
      sx={{
        fontSize: '0.875rem',
        backgroundColor: 'white',
        borderRadius: '5px',
        color: 'black',
        width: '90%',
        "& .MuiSelect-select": { padding: '7px 10px' },
        "& .MuiSvgIcon-root": { color: 'black' },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {renderValueMap ? renderValueMap[opt] : opt || "All"}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);



interface ImageFilter {
  checked: boolean
  imageCount: number
  operator: string
}

interface Filters {
  asset_id?: string;
  start_date?: string;
  end_date?: string;
  croppedImages: ImageFilter
  configuration?: string;
  bgrImages: ImageFilter
  exteriorImages: ImageFilter
  featuredImages: ImageFilter
  manualImages: ImageFilter
  panoramicImages: ImageFilter,
  ucImages: ImageFilter,
  pushed?: boolean | null;
  syndicated?: boolean | null;
  processingStatus: string;
  syndicationStatus: string;
  archived: boolean;
}

interface FiltersSidebarProps {
  toggleSidebar: () => void;
  setFilters: (filters: object) => void;
}

const defaultImageFilterValue = {
  checked: false,
  imageCount: 0,
  operator: '='
}

export const defaultFilters: Filters = {
  asset_id: undefined,
  start_date: undefined,
  end_date: undefined,
  croppedImages: defaultImageFilterValue,
  configuration: undefined,
  bgrImages: defaultImageFilterValue,
  exteriorImages: defaultImageFilterValue,
  featuredImages: defaultImageFilterValue,
  manualImages: defaultImageFilterValue,
  panoramicImages: defaultImageFilterValue,
  ucImages: defaultImageFilterValue,
  pushed: null,
  syndicated: null,
  processingStatus: "any",
  syndicationStatus: "any",
  archived: false
};

const FiltersSidebar: React.FC<FiltersSidebarProps> = ({ toggleSidebar, setFilters }) => {
  const [localFilters, setLocalFilters] = useState<Filters>(() => {
    try {
      const stored = sessionStorage.getItem('vehiclesNavState');
      if (!stored) return defaultFilters;
      const parsed = JSON.parse(stored);
      return parsed.filters || defaultFilters;
    } catch {
      return defaultFilters;
    }
  });
  const [localSystem, setLocalSystem] = useState<System | undefined>();
  const { selectedSystem, systems } = useSelector((state: State) => state.systemState);

  useEffect(() => {
    const fullSelectedSystem = systems?.find((sys: System) => sys.systemCode === selectedSystem)
    setLocalSystem(fullSelectedSystem)
  }, [selectedSystem])

  const handleChange = (field: keyof Filters, value: string | boolean | number | null | undefined | ImageFilter) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFilters(localFilters);
  };

  const clearFilters = () => {
    setLocalFilters(defaultFilters);
    setFilters(defaultFilters);
  };

  const comparisonOperators = ["=", "<", ">", "<=", ">="];
  const filtersApplied = (Object.keys(localFilters).length > 0) && (JSON.stringify(localFilters) !== JSON.stringify(defaultFilters))

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: theme => theme.palette.primary.main,
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          mb: 1,
          borderBottom: '1px solid #444', // optional styling for separation
        }}
      >
        <Typography variant="h6" sx={{ mr: 2 }}>
          Filters
        </Typography>
        <IconButton onClick={toggleSidebar}>
          <FilterAltOutlinedIcon
            sx={{
              color: filtersApplied
                ? 'darkred'
                : 'inherit'
            }}
          />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          pb: 2,
        }}
      >

        <TextFieldComponent
          label="VIN"
          type="text"
          value={localFilters.asset_id || ''}
          onChange={(e) => handleChange("asset_id", e.target.value)}
        />

        <Box sx={{ mb: 0.5, width: '90%' }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>
            Date Range (default 30 days)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              type="date"
              value={localFilters.start_date || ""}
              onChange={(e) => handleChange("start_date", e.target.value)}
              fullWidth
              sx={{
                backgroundColor: 'white',
                borderRadius: '5px',
                color: 'black',
                width: '90%',
                "& input": {
                  fontSize: '0.85rem',
                  padding: '7px 10px',
                  color: 'black',
                }
              }}
            />
            <TextField
              type="date"
              value={localFilters.end_date || ""}
              onChange={(e) => handleChange("end_date", e.target.value)}
              fullWidth
              sx={{
                backgroundColor: 'white',
                borderRadius: '5px',
                color: 'black',
                width: '90%',
                "& input": {
                  fontSize: '0.85rem',
                  padding: '7px 10px',
                  color: 'black',
                }
              }}
            />
          </Box>
        </Box>

        <DropdownField
          label="Configuration"
          value={localFilters.configuration ?? ''}
          onChange={(val) => handleChange("configuration", val)}
          options={["", ...(localSystem?.configurationNames || [])]}
        />


        <DropdownField
          label="Processing Status"
          value={localFilters.processingStatus}
          onChange={(val) => handleChange("processingStatus", val)}
          options={["any", "complete", "not complete"]}
          renderValueMap={{
            any: "Any",
            complete: "Complete",
            "not complete": "Not Complete"
          }}
        />

        <DropdownField
          label="Syndication Status"
          value={localFilters.syndicationStatus}
          onChange={(val) => handleChange("syndicationStatus", val)}
          options={["any", "complete", "failed", "partial success", "not complete"]}
          renderValueMap={{
            any: "Any",
            complete: "Complete",
            failed: "Failed",
            "partial success": "Partial Success",
            "not complete": "Not Complete"
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", mt: 1, mb: 0.3 }}>
          <Checkbox
            checked={localFilters.archived || false}
            onChange={(e) => handleChange("archived", e.target.checked)}
            size="small"
            sx={{ '&.Mui-checked': { color: "darkred" } }}
          />
          <Typography variant="body2" sx={{ ml: 1 }}>Show Archived</Typography>
        </Box>

        {[
          { label: "Cropped Images", field: "croppedImages" },
          { label: "Background Replaced", field: "bgrImages" },
          { label: "Exterior Images", field: "exteriorImages" },
          { label: "Featured Images", field: "featuredImages" },
          { label: "Manual Images", field: "manualImages" },
          { label: "Panoramic Images", field: "panoramicImages" },
          { label: "Undercarriage Images", field: "ucImages" },
        ].map(({ label, field }) => (
          <ImageFilterRow
            key={field}
            label={label}
            filter={localFilters[field as keyof Filters] as ImageFilter}
            comparisonOperators={comparisonOperators}
            onChange={(newValue) => handleChange(field as keyof Filters, newValue)}
          />
        ))}

        <Box sx={{ display: "flex", width: '100%', alignContent: "end", mt: 1.5, justifyContent: 'space-around' }}>
          <Button variant="outlined" color="secondary" onClick={clearFilters}>
            Clear
          </Button>
          <Button variant="contained" color="warning" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default FiltersSidebar;
